import { useEffect, useMemo } from 'react';
import { formatCoordinates, formatDistance } from '../game/geo';
import type { Round } from '../game/gameState';
import { getGameMap, type GameMapId } from '../game/maps';
import { formatScore, MAX_ROUND_SCORE, ROUNDS_PER_GAME, scoreVerdict } from '../game/scoring';
import { useAnimatedNumber } from '../hooks/useAnimatedNumber';
import { IconArrowRight, IconChart, IconExternal } from './Icons';
import MapView from './map/MapView';
import Attribution from './Attribution';

interface RoundResultProps {
  round: Round;
  roundIndex: number;
  totalScore: number;
  mapId: GameMapId;
  mapKind: 'leaflet' | 'google';
  onNext: () => void;
}

export default function RoundResult({ round, roundIndex, totalScore, mapId, mapKind, onNext }: RoundResultProps) {
  const animatedScore = useAnimatedNumber(round.score, 1100);
  const isLast = roundIndex + 1 >= ROUNDS_PER_GAME;
  const pairs = useMemo(() => [{ actual: round.panorama.position, guess: round.guess }], [round]);
  const { panorama } = round;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !(e.target instanceof HTMLButtonElement || e.target instanceof HTMLAnchorElement)) {
        e.preventDefault();
        onNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onNext]);

  return (
    <div className="result-screen" role="dialog" aria-modal="true" aria-labelledby="round-result-title">
      <MapView
        kind={mapKind}
        className="result-screen__map"
        initialView={getGameMap(mapId).view}
        pairs={pairs}
        ariaLabel="Carte du résultat : votre réponse et la position réelle"
      />
      <div className="result-card">
        <div className="result-card__head">
          <span className="eyebrow">
            Manche {roundIndex + 1} / {ROUNDS_PER_GAME}
          </span>
          <h2 id="round-result-title" className="result-card__verdict">
            {round.timedOut && !round.guess ? 'Temps écoulé !' : scoreVerdict(round.score)}
          </h2>
        </div>

        <div className="result-card__numbers">
          <div className="metric">
            <span className="metric__value metric__value--accent">{formatScore(animatedScore)}</span>
            <span className="metric__label">points sur {formatScore(MAX_ROUND_SCORE)}</span>
          </div>
          <div className="metric">
            <span className="metric__value">{round.distanceKm === null ? '—' : formatDistance(round.distanceKm)}</span>
            <span className="metric__label">{round.guess ? 'de la position réelle' : 'aucune réponse donnée'}</span>
          </div>
        </div>

        <div
          className="score-bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={MAX_ROUND_SCORE}
          aria-valuenow={round.score}
          aria-label="Score de la manche"
        >
          <span className="score-bar__fill" style={{ width: `${(animatedScore / MAX_ROUND_SCORE) * 100}%` }} />
        </div>

        <div className="result-card__location">
          <div>
            <span className="result-card__label">Position réelle</span>
            <span className="result-card__coords">{formatCoordinates(panorama.position)}</span>
          </div>
          <div className="result-card__links">
            <a
              className="link-btn"
              href={`https://www.openstreetmap.org/?mlat=${panorama.position.lat}&mlon=${panorama.position.lng}#map=16/${panorama.position.lat}/${panorama.position.lng}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Voir sur la carte <IconExternal size={14} />
            </a>
            {panorama.attribution.sourceUrl && (
              <a className="link-btn" href={panorama.attribution.sourceUrl} target="_blank" rel="noopener noreferrer">
                Voir l’image sur {panorama.attribution.sourceName} <IconExternal size={14} />
              </a>
            )}
          </div>
        </div>

        <Attribution attribution={panorama.attribution} detailed />

        <div className="result-card__footer">
          <span className="result-card__total">
            <IconChart size={16} /> Total : <strong>{formatScore(totalScore)}</strong> pts
          </span>
          <button type="button" className="btn btn--primary btn--lg" onClick={onNext} autoFocus>
            {isLast ? 'Voir le résumé' : 'Manche suivante'} <IconArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
